import React, { useState } from 'react';
import { useRole } from '../context/RoleContext';
import { useChat } from '../context/ChatContext';
import { useHistoryPanel } from './AppShell';
import { ArrowUp, ListOrdered, Square, Loader2 } from 'lucide-react';

const ChatComposer = () => {
  const { isProfessor } = useRole();
  const { sendMessage, isLoading, isStreaming, stopStreaming, selectedSubject } = useChat();
  const historyPanel = useHistoryPanel();
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (message.trim() && !isLoading && !isStreaming && selectedSubject) {
      const messageToSend = message;
      setMessage('');
      await sendMessage(messageToSend, true); // true = use streaming
    }
  };

  const handleKeyDown = (e) => {
    // Send on Enter, but not on Shift+Enter (for new lines)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const isDisabled = isLoading || isStreaming || !selectedSubject;

  return (
    <div 
      className={`chat-composer mt-auto ${isProfessor ? 'chat-composer-dark' : ''}`} 
      data-testid="chat-composer"
    >
      <form onSubmit={handleSubmit}>
        {/* Input Row */}
        <div className="flex items-center gap-3 mb-3">
          <input
            type="text"
            placeholder={selectedSubject ? "Comencemos..." : "Selecciona una asignatura..."}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isDisabled}
            className={`flex-1 bg-transparent text-sm focus:outline-none ${
              isProfessor 
                ? 'text-white placeholder:text-gray-500' 
                : 'text-gray-700 placeholder:text-gray-400'
            } ${isDisabled ? 'opacity-50' : ''}`}
            data-testid="composer-input"
          />
        </div>
        
        {/* Actions Row */}
        <div className="flex items-center justify-between">
          {/* Left side - Learning Plan button (hidden on desktop xl+) */}
          <div className="flex items-center">
            {historyPanel && (
              <button
                type="button"
                onClick={historyPanel.openHistoryPanel}
                className={`xl:hidden p-2 rounded-full transition-colors ${
                  isProfessor 
                    ? 'hover:bg-gray-700 text-gray-400 hover:text-white' 
                    : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                }`}
                data-testid="history-toggle-btn"
                aria-label="Open learning plan"
              >
                <ListOrdered className="w-5 h-5" />
              </button>
            )}
          </div>
          
          {/* Right side - Send button */}
          <div className="flex items-center gap-3">
            {/* Stop button when streaming */}
            {isStreaming ? (
              <button
                type="button"
                onClick={stopStreaming}
                className={`send-button ${isProfessor ? 'send-button-dark' : ''} !bg-red-500 hover:!bg-red-600`}
                data-testid="stop-btn"
                aria-label="Stop generating"
              >
                <Square className="w-4 h-4 fill-current" />
              </button>
            ) : (
              <button
                type="submit"
                disabled={!message.trim() || isDisabled}
                className={`send-button ${isProfessor ? 'send-button-dark' : ''} ${
                  (!message.trim() || isDisabled) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                data-testid="send-btn"
                aria-label="Send message"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <ArrowUp className="w-5 h-5" />
                )}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default ChatComposer;
